// This is the top-level Dapta app interface, which handles: 
// Project loading: onFileSelected
// Project saving: callLogObject 

<template>
  <div id="app-header">
    <button id="reset" @click="createNewObject(callResetObject, null)" :disabled="flowIslocked">
      <font-awesome-icon icon="plus-circle" size="lg" /> {{$t('message.New')}}
    </button>
    <button id="openItem" @click="openItem" :disabled="flowIslocked">
      <font-awesome-icon icon="folder-open" size="lg" /> {{$t('message.Open')}}
    </button>
    <button id="saveItem" @click="manualSaveToStore" :disabled="!loaded || flowIslocked">
      <font-awesome-icon icon="save" size="lg" /> {{ $t('message.Save')}}
    </button>
    <button id="downloadItem" @click="downloadItem" :disabled="!loaded">
      <font-awesome-icon icon="file-download" size="lg" /> {{$t('message.Download')}}
    </button>
    <button id="viewRunLog" @click="viewRunLog" :disabled="!logFound">
      <font-awesome-icon icon="eye" size="lg" /> {{$t('message.View_Log')}}
    </button>
    <button id="moreOptions" @click="getMoreOptions"  :disabled="!loaded">
      <font-awesome-icon icon="ellipsis" size="lg" /> </button>
    <button @click="logoutUser" style="float: right;">
      <font-awesome-icon icon="sign-out-alt" size="lg" /> {{$t('message.Log_out')}}
    </button>
      <button @click="toggleLanguage" style="float: right;"> 
      <font-awesome-icon icon="language" size="lg" /> En/中
    </button>
    <button @click="openInNewTab('https://daptadocs.com/index.html')"  style="float: right;">
      <font-awesome-icon icon="question-circle" size="lg" /> {{$t('message.Help')}} </button>
    <button class="inactive" style="float: right;"> {{ user.nickname }} </button>
  </div>
  <base-dialog
    key="viewRunStore"
    v-if="viewRunStore" 
    @close="viewRunStore = false;"
    visibility>
      <template #default>
        <h3 style="display: flex; justify-content: space-between; align-items: center; text-align: left; padding-left: 20px;">
          <span>{{ $t('message.Saved_Runs') }}</span>
          <button id="language" @click="toggleLanguage" style="float: right; background-color: black; color: white;"> 
            <font-awesome-icon icon="language" size="lg" /> En / 中
          </button>
        </h3>
        <ul style="list-style: none; text-align: left; padding: 0; margin: 0;  ">
          <li v-for="item in allRuns" :key="item.name" class="option">
            <img :src="item.thumbnail" alt="workspace view" class="option-thumbnail">
            <p class="option-description"> 
              {{item.name}}
              <br>
              <em> {{$t('message.Last_saved')}} {{item.mtime}} </em>
            </p>
            <div class="option-buttons">
              <button class="basedialog" @click="async() => await callLoadRunFromStore(item.name)" :disabled="runName === item.name"> {{$t('message.Load')}} </button> 
              <button class="basedialog" @click="async () => await renameRunFolder(item.name, item.name)"> {{$t('message.Duplicate')}} </button> 
              <button class="basedialog" @click="getNameThenRename(item.name)"> {{ $t('message.Rename')}} </button> 
              <!-- <button class="basedialog" :disabled="true"> View Versions </button> 
              <button class="basedialog" :disabled="true"> Share </button>  -->
              <button class="basedialog" @click="async() => await deleteRunFromStore(item.name)"> {{$t('message.Delete')}} </button> 
            </div>
          </li>
          <br>
          <li class="option">
            <button class="basedialog" @click="createNewObject(callResetObject, null)"> {{$t('message.New')}} </button> 
            <p class="option-description"> 
              {{$t('message.Create_a_new_Run')}}
            </p>
          </li>
          <li class="option">
            <label for="fileInput" class="custom-file-input"> {{ $t('message.Import')}}  </label>
            <input id="fileInput" type="file" @change="(ev) => {createNewObject(onFileSelected, ev)}" ref="fileInput"/>
            <p class="option-description"> 
              {{$t('message.Import_a_run_from_file')}}
            </p>
          </li>
        </ul> 
      </template>
  </base-dialog>
  <small-dialog
    key="viewGetRunName"
    v-if="viewGetRunName"
    :callback="smallDialog.callback" 
    :callback-args="smallDialog.callbackArgs"
    :heading="smallDialog.heading"
    :on-cancel="smallDialog.onCancel"
    :run-name="runName"
    visibility>
  </small-dialog>
  <div v-if="loaded" id="vue-flow">
    <CustomNode 
    ref="flowView" 
    v-bind="workflow" 
    :isnamespace="isNamespace" 
    :viewlog="viewlog" 
    :more-options="moreOptions"
    :hide-on-boarding-msg="hideOnBoardingMsg"
    :run-name="runName"
    @load-from-store="(name, opt) => callLoadRunFromStore(name,opt.beApiTerminate)"
    @loading="(val) => {loading = val}"
    @flow-save="saveToFile" 
    @flow-cache="cacheToLocalstorage"
    @log-found="(data) => { logFound = data}"
    @close-log="viewlog = false; moreOptions = false; viewRunStore = false; "
    @toggle-lock="toggleFlowLock"
    @toggle-on-boarding-msg="(val) => { toggleOnBoardingMsg(val) }"
    @reset-run="moreOptions = false; onTerminateRun()"></CustomNode>
  </div>
  <div v-show="!loaded || loading" class="loading">
    <img src="../assets/Dapta-Brandmark-RGB.png" class="logo rotate" width="70" height="70" />
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, reactive, toRefs, inject, watch } from "vue";
import { useAuth0 } from '@auth0/auth0-vue';
import Shared from './shared.vue'
import BaseDialog from "./BaseDialog.vue";
import SmallDialog from "./SmallDialog.vue";
import userResourceQuotas from "./UserResourceQuotas.json"
import tutorials from "./Tutorials.json"
import { useRouter } from 'vue-router'

const props = defineProps({
  tutorial: String,
});
const router = useRouter();
const { user, logout } = useAuth0();
import CustomNode from "./CustomEditableNode.vue";
const loaded = ref(false);
const workflow = ref();
let fileInput = ref();
let flowView = ref();
const k8sAdminAPIEndPoint = inject('back_end_admin_API_end_point');
const userJWTToken = inject('userJWTToken');
const isNamespace = ref(false)
const logFound = ref(false)
const viewlog = ref(false)
const flowIslocked = ref(true)
const moreOptions = ref(false)
const hideOnBoardingMsg = ref(false)
const loading = ref(false)
const runName = ref()
const viewRunStore = ref(false)
const allRuns = ref()
const viewGetRunName = ref(false)

// add new codes for change language
import { useI18n } from 'vue-i18n';
const { locale, t } = useI18n();
const toggleLanguage = () => {
  locale.value = locale.value === 'en' ? 'zh' : 'en';
}

const manualSaveToStore = async () => {
  flowIslocked.value = true;
  loaded.value && await saveRunToStore();
  flowIslocked.value = false;
}

const openItem = async () => { 
  allRuns.value = await Shared.methods.call_be_api_getstoreitems(k8sAdminAPIEndPoint, userJWTToken.value, "runs")
  viewRunStore.value = true;
}

const runNameIsUnique = (name) => { 
  if (allRuns.value.filter(e => e.name === name).length) { 
    return false
  }
  return true
}

const onSetRunName = (name, args) => { 
  if (runNameIsUnique(name)) {
    runName.value = name;
    viewGetRunName.value = false;
    loading.value = false;
    loaded.value = true;
  } else { 
    alert("\u274C This name is already associated with a stored run.\nPlease choose another name.")
  }
}
const onLoadTutorial = async (name, args) => { 
  if (runNameIsUnique(name)) {
    runName.value = name;
    viewGetRunName.value = false;
    await renameRunFolder("_scr_" + tutorials[props.tutorial].name, runName.value)
    router.push('/interface')
    const flow = await Shared.methods.call_be_api_download(k8sAdminAPIEndPoint, userJWTToken.value, runName.value)
    flow.run_parameters.name = runName.value
    workflow.value = flow 
    loading.value = false;
    loaded.value = true;
  } else { 
    alert("\u274C This name is already associated with a stored run.\nPlease choose another name.")
  }  
}
const cancelGetRunName = () => { 
  console.log("Cancel GetRunName.")
  viewGetRunName.value = false;
  viewRunStore.value = true;
  loading.value = false;
}
const smallDialog = ref({
  callback: onSetRunName,
  callbackArgs: {},
  heading: "Create Run",
  onCancel: cancelGetRunName
})

const onFileSelected = async (ev) => {
  const file = ev.target.files[0];
  const reader = new FileReader();

  reader.addEventListener("load", (e) => {
    try {
      if (typeof reader.result !== "string")
        throw "FileReader did not return string.";
      workflow.value = JSON.parse(reader.result);
      if (workflow.value.run_parameters?.name) {
        runName.value = workflow.value.run_parameters?.name
      } else { 
        runName.value = ""
      }
      console.log("loaded from file:", workflow.value);
    } catch (ex) {
      console.log("ex when trying to parse json =" + ex);
    }
  });
  if (file) reader.readAsText(file);

  console.log(`Input file ${file.path} read-in successfully.`);
};

const saveToFile = async (data : string, filename : string) => {
  const blob = new Blob([data], {type : 'application/json'});
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob)
  link.download = filename;
  link.dataset.downloadurl = ['text/json', link.download, link.href].join(':');
  link.click();
  window.URL.revokeObjectURL(link.href);
};

const toggleFlowLock = (data) => { 
  flowIslocked.value = data
}

const downloadItem = async () => {
  await flowView.value.logToObject(true);
};



const createNewObject = async (objCreateCallback, ev) => { 
  if (allRuns.value.length >= Number(userResourceQuotas.runs)) {
    alert("\u26A0 Warning: You have reached the maximum number of runs.\nDelete a saved run before creating a new one.")
    if (fileInput.value?.type) { 
      // change type to reset event
      fileInput.value.type = "text";
      fileInput.value.type = "file";
    }
    return
  }
  loading.value = true; 
  viewRunStore.value = false;
  if (await terminateOnUserConfirm()) {
    loading.value = false; 
    return;
  }
  loaded.value = false; 
  smallDialog.value.callback = onSetRunName
  smallDialog.value.heading = t('message.Create_Run') 
  smallDialog.value.callbackArgs = {}
  await objCreateCallback(ev)
  viewGetRunName.value = true;
}

const callResetObject = async (ev) => {
  runName.value = ""
  workflow.value = JSON.parse(
    '{"components": [], "connections": [], "workflow": {},  "run_parameters": {}}'
  );    
  console.log("loaded from file:", workflow.value);
};

const callGetTutorial = async () => { 
  console.log("load tutorial: ", props.tutorial)
  runName.value = tutorials[props.tutorial].name
  // load dummy workflow
  workflow.value = JSON.parse(
    '{"components": [], "connections": [], "workflow": {},  "run_parameters": {}}'
  );
  smallDialog.value.callback = onLoadTutorial
  smallDialog.value.heading = `Create Run from Tutorial #${props.tutorial}`
  smallDialog.value.callbackArgs = {}    
}

const viewRunLog = () => { viewlog.value = true; }

const getMoreOptions = () => { moreOptions.value = true; }

const logoutUser = () => {  
  logout({ returnTo: "https://www.dapta.com/wp-login.php?action=logout" });
  }

const setUpTutorial = async () => { 
  if (!(props.tutorial in tutorials)) { 
    alert("The requested tutorial ID doesn't exist.")
    return false
  }
  await createNewObject(callGetTutorial, null)
  if (workflow.value) {
    return true
    }
  return false
} 

const callLoadActiveObject = async () => { 
  // read input json from server and load vueflow
  if (props.tutorial && await setUpTutorial()) {
    return;
    }
  if (isNamespace.value) {
    workflow.value = await Shared.methods.call_be_api_download(k8sAdminAPIEndPoint, userJWTToken.value, runName.value)
  } else if (localStorage.currentRun) {
    workflow.value = JSON.parse(localStorage.currentRun)
    runName.value = workflow.value["run_parameters"]["name"]
  } else { 
    await openItem()
    return;
  }
  console.log("loaded from file:", workflow.value);
  loaded.value = true;
}

const callLoadRunFromStore = async (name, beApiTerminate = true) => { 
  loading.value = true;
  viewRunStore.value = false;
  if (runName.value !== name) {
    if (await terminateOnUserConfirm(true, beApiTerminate)) {
      loading.value = false; 
      viewRunStore.value = true;
      return;
    }
    runName.value = name
  }
  workflow.value = await Shared.methods.call_be_api_download(k8sAdminAPIEndPoint, userJWTToken.value, name)
  workflow.value.run_parameters.name = runName.value
  console.log("loaded from file:", workflow.value);
  loading.value = false;
  loaded.value = true;
}

const onTerminateRun = async () => { 
  // delete the user namespace without deleting files
  loading.value = true;
  if (await terminateOnUserConfirm()) {
    loading.value = false; 
    return;
  }
  loaded.value = false
  runName.value = ""
  workflow.value = null
  loading.value = false;
  await callLoadActiveObject()
}

const deleteRunFromStore = async (name) => { 
  loading.value = true;
  if (name === runName.value) { 
    viewRunStore.value = false;
    if (await terminateOnUserConfirm(false)) {
      loading.value = false; 
      viewRunStore.value = true;
      return;
    }
    runName.value = ""
    loaded.value = false;
  }
  await Shared.methods.call_be_api_delete_files(k8sAdminAPIEndPoint, userJWTToken.value, null, null, null, name);
  allRuns.value = await Shared.methods.call_be_api_getstoreitems(k8sAdminAPIEndPoint, userJWTToken.value, "runs")
  viewRunStore.value = true;
  console.log("deleted run: ", name);
  loading.value = false;
}

const terminateOnUserConfirm = async (autosave = true, beApiTerminate = true) => { 
  if (beApiTerminate) {
    isNamespace.value = await Shared.methods.call_get_Namespace_exists(k8sAdminAPIEndPoint, userJWTToken.value, "any")
  }
  if ( beApiTerminate && isNamespace.value == true) {
    if (confirm("Warning: \nThe existing run cluster will be stopped and run logs will be deleted. \n\nDo you want to continue?") == true) {
      flowIslocked.value = true;
      logFound.value = false;
      await Shared.methods.call_be_api_terminate(k8sAdminAPIEndPoint, userJWTToken.value)
      if (loaded.value && autosave) {
        await saveRunToStore()
        const canvasBlob = await Shared.methods.getScreenshot()
        await Shared.methods.uploadScreenshot(k8sAdminAPIEndPoint, userJWTToken.value, runName.value, canvasBlob)
      }
      if (localStorage.currentRun) {
        localStorage.currentRun = ""
      };
      isNamespace.value = false;
      flowIslocked.value = false;
      return false;
    } else {
      console.log("cluster reset cancelled by user -> exiting current function call.")
      return true;
    }
  } else if (loaded.value) { 
    flowIslocked.value = true;  
    if (autosave) {
      await saveRunToStore()
      const canvasBlob = await Shared.methods.getScreenshot()
      await Shared.methods.uploadScreenshot(k8sAdminAPIEndPoint, userJWTToken.value, runName.value, canvasBlob)
    }
    if (localStorage.currentRun) {
      localStorage.currentRun = ""
    };
    flowIslocked.value = false;
    return false;
  }
}

const saveRunToStore = async () => { 
  await flowView.value.logToObject(false);
  await flowView.value.call_be_api_upload()
  allRuns.value = await Shared.methods.call_be_api_getstoreitems(k8sAdminAPIEndPoint, userJWTToken.value, "runs")
}

const renameRunFolder = async (source, target) => { 

  loading.value = true;
  allRuns.value = await Shared.methods.call_be_api_getstoreitems(k8sAdminAPIEndPoint, userJWTToken.value, "runs")

  let method = "";
  if (source === target) {
    method = "duplicate";
    target = target + "-copy"
  } else if (source.startsWith("_scr_")) { 
    method = "tutorial";
    source = source.substr(5)
  }

  if (allRuns.value.filter(e => e.name === target).length) { 
    alert(`\u274C A run with the name ${target} already exists. Delete or rename that run first.`)
    loading.value = false;
    return
  }

  const data = new FormData()
  data.append('source', source);
  data.append('target', target);
  if (method) { 
    data.append('method', method);
  }
  const resp = await fetch(k8sAdminAPIEndPoint + "/renamerunfolder", {
    method: "POST",
    headers: { "Authorization": `Bearer ${userJWTToken.value}`},
    body: data
  }).then(Shared.methods.handleErrors)
    .then(response => { return response.text() })
  console.log(resp)

  allRuns.value = await Shared.methods.call_be_api_getstoreitems(k8sAdminAPIEndPoint, userJWTToken.value, "runs")
  loading.value = false;
}

const onRunReName = async (name, args) => { 
  if (runNameIsUnique(name)) {
    viewGetRunName.value = false;
    if (args["source"] === runName.value) { 
      console.warn("renaming loaded run")
      if (await terminateOnUserConfirm()) {
        loading.value = false; 
        return;
      }
      runName.value = ""
      loaded.value = false
    }  
    await renameRunFolder(args["source"], name)
    viewRunStore.value = true;
    loading.value = false;
  } else { 
    alert("\u274C This name is already associated with a stored run.\nPlease choose another name.")
  }
}

const getNameThenRename = (source) => { 
  loading.value = true;
  smallDialog.value.callback = onRunReName
  smallDialog.value.callbackArgs = { "source": source }
  smallDialog.value.heading = "Rename Run"
  viewGetRunName.value = true;
  viewRunStore.value = false;
}

const openInNewTab = (url) => {
      window.open(url, '_blank', 'noreferrer');
    }

const localStorageAvailable = () => {
    if (typeof(Storage) !== "undefined") {
        return true;
    }
    else {
        return false;
    }
}

const setOnBoardingMsg = () => {
  if (localStorageAvailable()) {
        if (localStorage.hideOnBoardingMsg && localStorage.hideOnBoardingMsg === "true") {
            hideOnBoardingMsg.value = true
        }
    };
}
setOnBoardingMsg()

const cacheToLocalstorage = async (data: string) => { 
  if (localStorageAvailable()) {
        console.log("caching current run")
        localStorage.currentRun = String(data);
    };  
}


const createChat = () => { 
  let smartsuppScript = document.createElement('script')
  smartsuppScript.src = process.env.BASE_URL + "smartsupp.js"
  document.head.appendChild(smartsuppScript)
  let scriptIsReady = false;
  let checkInterval = setInterval(function () {
      if (window.smartsupp) {
        scriptIsReady = true;
        clearInterval(checkInterval);
        console.log("sending data to smartsupp")
        window.smartsupp('name', user.value.nickname);
        window.smartsupp('email', user.value.email);
      }
      return scriptIsReady;
    }, 500);
}

const toggleOnBoardingMsg = (val) => { 
  console.log("set hideOnBoardingMsg message to ", val)
  if (localStorageAvailable()) {
    localStorage.hideOnBoardingMsg = String(val);
  }
}

watch(
  () => [props.tutorial],
  (newval, oldval) => {
   console.log("props.tutorial update: ", newval, oldval)
  }
);

onMounted(async () => {
  [isNamespace.value, runName.value] = await Shared.methods.call_get_Namespace_exists(k8sAdminAPIEndPoint, userJWTToken.value, "any", true)
  allRuns.value = await Shared.methods.call_be_api_getstoreitems(k8sAdminAPIEndPoint, userJWTToken.value, "runs")
  await callLoadActiveObject()
  flowIslocked.value = false;
  createChat()
  console.warn("mounted Interface");
})

</script>

<style>
/* TODO add style for disabled button */

html {
  font-family: sans-serif;
}

html,
body {
  height: 100%;
  margin: 0;
}

#app {
  font-family: Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif,
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: rgb(66, 66, 66);
  margin-top: 0px;
}

#vue-flow {
  width: 100%;
  position: absolute;
  top: 50px;
  bottom: 50px;
  z-index: 0;
}

#app-header {
  position: absolute;
  top: 0px;
  background-color: rgb(66, 66, 66);
  height: 40px;
  width: 100%;
  margin: 0px;
}

.loading {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.75);
  z-index: 10;
}

input[type="file"] {
  display: none;
}

button {
  font: inherit;
  border: 1px solid #ccc;
  background-color: transparent;
  display: inline-block;
  padding: 0.2rem 1rem;
  border-radius: 30px;
  cursor: pointer;
  color: white;
  margin: 5px;
  float: left;
}


button:hover,
button:active {
  border-color: #ccc;
  background-color: #ccc;
  color: rgb(66, 66, 66);
}


button:disabled, button:disabled:hover {
  color: rgb(148, 148, 148);
  border-color: rgb(148, 148, 148);
  background-color: transparent;
}

.inactive, .inactive:hover, .inactive:active {
  border: none;
  background-color: transparent;
  color: white;
}

.rotate {
  animation: rotation 3s infinite linear;
}

.logo {
  display: block;
  margin-left: auto;
  margin-right: auto;
  margin-top:  200px;
}

@keyframes rotation {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(359deg);
  }
}

button.basedialog {
    font: inherit;
    border: 1px solid #0076bb;
    background-color: #0076bb;
    color: white;
    cursor: pointer;
    padding: 0.75rem 2rem;
    border-radius: 30px;
    width: 180px;
  }

.custom-file-input{
    font: inherit;
    border: 1px solid #0076bb;
    background-color: #0076bb;
    color: white;
    cursor: pointer;
    padding: 0.75rem 2rem;
    border-radius: 30px;
    width: 115px; 
    display: inline;
    margin: 5px;
    text-align: center;
    float:left
}

.custom-file-input:hover,
.custom-file-input:active,
button:hover.basedialog,
button:active.basedialog {
    border-color: #013274;
    background-color: #013274;
    color: white;
  }

.custom-file-input:disabled,
button:disabled.basedialog {
    border: 1px solid #0076bb70;
    background-color: #0076bb70;
    color: white;
  }
  
.option-description {
  text-align: left; 
  display: block;
  width: 30%;
  padding-left: 2%;
  float: left;
}
.option {
  display: inline-block;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.5);
  padding-top: 5px;
  padding-bottom: 5px;
  margin:0;
}

.option-buttons {
  display: block;
  float: right;
  width: 45%;
}

.option-thumbnail {
  display: block;
  float: left;
  width: 20%;
  padding-left: 2%;
  height: 150px;
}

input, 
select {
  display: block;
  margin-left: 3%;
  width: 93%;
  font: inherit;
  margin-top: 0.5rem;
}

</style>
